// One request per user, shared by every caller that wants the same answer.
//
// The shape keeps turning up: an endpoint whose response depends only on who is
// signed in, asked for independently by a router guard, a store, a layout
// component and a global widget — each with its own private cache, so none can
// see the others. Sentry sees it as Consecutive HTTP: the same URL two or three
// times in one pageload (BS-WEB-APP-16P for the subscription endpoint,
// BS-WEB-APP-16V for the company profile).
//
// Two layers, because the calls are usually CONSECUTIVE rather than concurrent
// — the guard resolves long before the header mounts, so joining in-flight
// requests alone collapses nothing:
//   - in-flight join: concurrent callers share one request, always.
//   - short TTL: a caller arriving just after a request settled reuses the
//     answer instead of re-asking.
//
// `maxAge: 0` opts out of the TTL (never out of the in-flight join) for a caller
// that must not act on a cached answer.

export interface PerUserRequest<T> {
    /** Fetch for this user, reusing a recent or in-flight answer where allowed. */
    fetch(userId: number | string, opts?: { maxAge?: number }): Promise<T>;
    /** Drop everything. For sign-out, and for the same id signing back in. */
    reset(): void;
}

export function createPerUserRequest<T>(
    request: (userId: string) => Promise<T>,
    ttlMs: number
): PerUserRequest<T> {
    let cachedUserId: string | null = null;
    let cachedValue: T | null = null;
    let cachedAt = 0;
    let inFlight: Promise<T> | null = null;

    function reset(): void {
        cachedUserId = null;
        cachedValue = null;
        cachedAt = 0;
        inFlight = null;
    }

    function fetch(userId: number | string, opts: { maxAge?: number } = {}): Promise<T> {
        const key = String(userId);
        const maxAge = opts.maxAge ?? ttlMs;

        // An id change means the signed-in account changed. Nothing cached for
        // the previous one may be handed to the new one.
        if (key !== cachedUserId) {
            reset();
            cachedUserId = key;
        }

        if (maxAge > 0 && cachedValue !== null && Date.now() - cachedAt < maxAge) {
            return Promise.resolve(cachedValue);
        }

        // Join rather than race, even for maxAge 0: a request already in flight
        // was started microseconds ago and is as fresh as a new one would be.
        if (inFlight) return inFlight;

        inFlight = request(key)
            .then((value) => {
                // Only successes are cached. A failure is not an answer, and
                // every caller of these endpoints fails open — caching it would
                // hand the same wrong "unknown" to the next caller for a whole
                // TTL.
                cachedValue = value ?? null;
                cachedAt = Date.now();
                return value;
            })
            .finally(() => {
                inFlight = null;
            });

        return inFlight;
    }

    return { fetch, reset };
}
